import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ActionIcon, Badge, Button, Group, Modal, TextInput, Title, Tooltip } from '@mantine/core';
import AdminUserForm, { type AdminUserFormValues } from '@components/admin-user-form';
import AdminUserExpandedDetails from '@components/admin-user-expanded-details';
import AdminUserRowActions from '@components/admin-user-row-actions';
import GraphqlError from '@components/graphql-error';
import LoadingData from '@components/graphql-loading-data';
import {
  ADMIN_CREATE_USER,
  ADMIN_DISABLE_USER,
  ADMIN_ENABLE_USER,
  ADMIN_LIST_USERS,
  ADMIN_RESET_USER_PASSWORD,
  ADMIN_UPDATE_USER_ATTRIBUTES,
} from '@queries/admin-users';
import type { AdminListUsersQuery } from '@src/__generated__/graphql';
import { IconRefresh, IconSearch } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';

const statusColors: Record<string, string> = {
  active: 'green',
  inactive: 'gray',
  confirmed: 'green',
  force_change_password: 'orange',
  unconfirmed: 'yellow',
  archived: 'gray',
  unknown: 'gray',
};

const billingStatusColors: Record<string, string> = {
  active: 'green',
  trialing: 'blue',
  'past-due': 'red',
  unpaid: 'orange',
  canceled: 'gray',
  unknown: 'gray',
};

type AdminUserSummary = AdminListUsersQuery['adminListUsers']['items'][number];

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleString();
};

const renderConfirmStatusBadge = (record: AdminUserSummary) => (
  <Badge color={statusColors[record.confirmStatus?.toLowerCase() || 'unknown'] || 'gray'} variant="light">
    {record.confirmStatus || '-'}
  </Badge>
);

const renderBillingStatusBadge = (record: AdminUserSummary) => (
  <Badge color={billingStatusColors[record.billingStatus?.toLowerCase() || 'unknown'] || 'gray'} variant="light">
    {record.billingStatus || '-'}
  </Badge>
);

function AdminUsers() {
  const { loading, error, data, refetch } = useQuery<AdminListUsersQuery>(ADMIN_LIST_USERS);
  const [createUser, { loading: createUserLoading }] = useMutation(ADMIN_CREATE_USER);
  const [updateUserAttributes, { loading: updateUserLoading }] = useMutation(ADMIN_UPDATE_USER_ATTRIBUTES);
  const [enableUser, { loading: enableLoading }] = useMutation(ADMIN_ENABLE_USER);
  const [disableUser, { loading: disableLoading }] = useMutation(ADMIN_DISABLE_USER);
  const [resetUserPassword, { loading: resetPasswordLoading }] = useMutation(ADMIN_RESET_USER_PASSWORD);
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUserSummary | null>(null);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<AdminUserSummary>>({
    columnAccessor: 'familyName',
    direction: 'asc',
  });
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const records = useMemo(() => data?.adminListUsers.items || [], [data]);

  const filteredRecords = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return records;

    return records.filter((record) => {
      const familyName = (record.familyName || '').toLowerCase();
      const givenName = (record.givenName || '').toLowerCase();
      const email = (record.email || '').toLowerCase();

      return (
        familyName.includes(normalizedSearch) ||
        givenName.includes(normalizedSearch) ||
        email.includes(normalizedSearch)
      );
    });
  }, [records, searchTerm]);

  const sortedRecords = useMemo(() => {
    const sorted = [...filteredRecords].sort((a, b) => {
      const getSortValue = (record: AdminUserSummary) => {
        if (sortStatus.columnAccessor === 'enabled') {
          return record.enabled ? 1 : 0;
        }

        if (sortStatus.columnAccessor === 'lastSeenAt') {
          const timestamp = record.lastSeenAt ? new Date(record.lastSeenAt).getTime() : 0;
          return Number.isNaN(timestamp) ? 0 : timestamp;
        }

        const rawValue = record[sortStatus.columnAccessor as keyof AdminUserSummary];
        return typeof rawValue === 'string' ? rawValue.toLowerCase() : (rawValue ?? '').toString();
      };

      const valueA = getSortValue(a);
      const valueB = getSortValue(b);

      if (valueA < valueB) return -1;
      if (valueA > valueB) return 1;
      return 0;
    });

    if (sortStatus.direction === 'desc') sorted.reverse();
    return sorted;
  }, [filteredRecords, sortStatus]);

  const isToggling = enableLoading || disableLoading;

  const handleToggleUser = async (record: AdminUserSummary) => {
    setPendingActionKey(`${record.username}:toggle`);

    try {
      if (record.enabled) {
        await disableUser({
          variables: { username: record.username },
          refetchQueries: [{ query: ADMIN_LIST_USERS }],
          awaitRefetchQueries: true,
        });
      } else {
        await enableUser({
          variables: { username: record.username },
          refetchQueries: [{ query: ADMIN_LIST_USERS }],
          awaitRefetchQueries: true,
        });
      }
    } finally {
      setPendingActionKey(null);
    }
  };

  const handleResetPassword = async (record: AdminUserSummary) => {
    setPendingActionKey(`${record.username}:reset`);

    try {
      await resetUserPassword({
        variables: { username: record.username },
      });
    } finally {
      setPendingActionKey(null);
    }
  };

  const handleCreateUser = async (values: AdminUserFormValues) => {
    const fallbackDisplayName = `${values.firstName.trim()} ${values.lastName.trim()}`.trim();

    await createUser({
      variables: {
        email: values.email.trim(),
        displayName: values.displayName.trim() || fallbackDisplayName || null,
        givenName: values.firstName.trim() || null,
        familyName: values.lastName.trim() || null,
        sendInvite: values.sendInvite,
        markEmailVerified: values.markEmailAsVerified,
        enableTrial: values.enableTrial,
      },
      refetchQueries: [{ query: ADMIN_LIST_USERS }],
      awaitRefetchQueries: true,
    });

    setCreateModalOpened(false);
  };

  const handleEditUser = async (values: AdminUserFormValues) => {
    if (!editingUser?.username) return;

    const trimmedFirstName = values.firstName.trim();
    const trimmedLastName = values.lastName.trim();
    const fallbackDisplayName = `${trimmedFirstName} ${trimmedLastName}`.trim();
    const trimmedDisplayName = (values.displayName.trim() || fallbackDisplayName).trim();

    const variables: {
      username: string;
      displayName?: string;
      givenName?: string;
      familyName?: string;
    } = {
      username: editingUser.username,
    };

    if (trimmedDisplayName) variables.displayName = trimmedDisplayName;
    if (trimmedFirstName) variables.givenName = trimmedFirstName;
    if (trimmedLastName) variables.familyName = trimmedLastName;

    if (!variables.displayName && !variables.givenName && !variables.familyName) {
      setEditModalOpened(false);
      setEditingUser(null);
      return;
    }

    console.log('Updating user with variables:', variables);
    try {
      const result = await updateUserAttributes({
        variables,
        refetchQueries: [{ query: ADMIN_LIST_USERS }],
        awaitRefetchQueries: true,
        errorPolicy: 'all',
      });

      if (result.errors && result.errors.length > 0) {
        console.error('Admin update user GraphQL errors:', {
          errors: result.errors,
          errorsJson: JSON.stringify(result.errors),
        });
        return;
      }

      setEditModalOpened(false);
      setEditingUser(null);
    } catch (mutationError) {
      const apolloError = mutationError as {
        graphQLErrors?: unknown[];
        networkError?: { result?: unknown };
        message?: string;
      };

      console.error('Admin update user failed:', {
        message: apolloError?.message,
        graphQLErrors: apolloError?.graphQLErrors,
        networkError: apolloError?.networkError,
        networkResult: apolloError?.networkError?.result,
      });
    }
  };

  const openEditModal = (record: AdminUserSummary) => {
    setEditingUser(record);
    setEditModalOpened(true);
  };

  const handleRefreshUsers = async () => {
    setIsRefreshing(true);

    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCopy = async (key: string, value?: string | null) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((current) => (current === key ? null : current));
      }, 1500);
    } catch (error) {
      console.error('Failed to copy value to clipboard', error);
    }
  };

  if (loading) return <LoadingData />;
  if (error) return <GraphqlError error={error} />;

  return (
    <>
      <Title order={2} pb={20}>
        USERS
      </Title>

      <Group justify="space-between" pt={25} pb={35}>
        <TextInput
          w={300}
          leftSection={<IconSearch />}
          placeholder="Search by last, first, email"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
        />
        <Group gap="sm">
          <Tooltip label="Refresh users">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={handleRefreshUsers}
              loading={isRefreshing}
              aria-label="Refresh users"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button onClick={() => setCreateModalOpened(true)}>+ NEW USER</Button>
        </Group>
      </Group>

      <Modal opened={createModalOpened} onClose={() => setCreateModalOpened(false)} title="Create User" centered>
        <AdminUserForm
          mode="create"
          loading={createUserLoading}
          onCancel={() => setCreateModalOpened(false)}
          onSubmit={handleCreateUser}
        />
      </Modal>

      <Modal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          setEditingUser(null);
        }}
        title="Edit User"
        centered
      >
        {editingUser && (
          <AdminUserForm
            mode="edit"
            loading={updateUserLoading}
            initialValues={{
              email: editingUser.email || '',
              firstName: editingUser.givenName || '',
              lastName: editingUser.familyName || '',
              displayName: editingUser.displayName || '',
            }}
            onCancel={() => {
              setEditModalOpened(false);
              setEditingUser(null);
            }}
            onSubmit={handleEditUser}
          />
        )}
      </Modal>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        records={sortedRecords}
        idAccessor="username"
        rowExpansion={{
          allowMultiple: false,
          content: ({ record }) => (
            <AdminUserExpandedDetails record={record} copiedKey={copiedKey} onCopy={handleCopy} />
          ),
        }}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          {
            accessor: 'familyName',
            title: 'Last Name',
            sortable: true,
            render: (record) => record.familyName || '-',
          },
          {
            accessor: 'givenName',
            title: 'First Name',
            sortable: true,
            render: (record) => record.givenName || '-',
          },
          {
            accessor: 'email',
            title: 'Email',
            sortable: true,
            render: (record) => record.email || '-',
          },
          {
            accessor: 'confirmStatus',
            title: 'Confirm Status',
            sortable: true,
            render: (record) => renderConfirmStatusBadge(record),
          },
          {
            accessor: 'enabled',
            title: 'Enabled',
            sortable: true,
            render: (record) => (
              <Badge color={record.enabled ? 'green' : 'red'} variant="light">
                {record.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            ),
          },
          {
            accessor: 'billingStatus',
            title: 'Billing Status',
            sortable: true,
            render: (record) => renderBillingStatusBadge(record),
          },
          {
            accessor: 'lastSeenAt',
            title: 'Last Seen',
            sortable: true,
            render: (record) => formatDate(record.lastSeenAt),
          },
          {
            accessor: 'actions',
            title: 'Actions',
            render: (record) => (
              <AdminUserRowActions
                record={record}
                isToggling={isToggling}
                resetPasswordLoading={resetPasswordLoading}
                pendingActionKey={pendingActionKey}
                onEdit={openEditModal}
                onToggle={handleToggleUser}
                onResetPassword={handleResetPassword}
              />
            ),
          },
        ]}
      />
    </>
  );
}

export default AdminUsers;
